import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useQuery } from '@apollo/client';
import {
	Area,
	AreaChart,
	CartesianGrid,
	XAxis,
	YAxis
} from 'recharts';
import {
	eachDayOfInterval,
	endOfDay,
	format,
	isValid,
	parseISO,
	startOfDay,
	subDays
} from 'date-fns';
import {
	Alert,
	AlertDescription,
	AlertTitle
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent
} from '@/components/ui/chart';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';
import {
	BarChart3,
	CalendarDays,
	CheckCircle2,
	Link2,
	LogIn,
	LogOut,
	Ticket,
	Users
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DASHBOARD_METRICS_QUERY } from './dashboard.gql';
import {
	formatCustomerName,
	formatDateTime
} from '../parking-sessions/utils';
import { ParkingSessionDetailsQuery } from '../parking-sessions/parking-session-details-query';

type CountNode = { itemsCount?: number | null };
type EntryNode = { entryDate: string; itemsCount?: number | null };

type InsideReservationSession = {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	vehicle?: {
		plateNumber?: string | null;
		make?: string | null;
		model?: string | null;
		color?: string | null;
	} | null;
	customer?: {
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
	} | null;
	reservations?: Array<{
		reservationId: string;
		reservationStartTime: string;
		reservationEndTime: string;
		reservationStatus?: { name?: string | null } | null;
	}> | null;
};

interface DashboardMetricsData {
	recentEntries?: { nodes?: EntryNode[] | null } | null;
	todayEntries?: { nodes?: CountNode[] | null } | null;
	todayExits?: { nodes?: CountNode[] | null } | null;
	parkingSessionsPerStatus?: Array<
		| { isInside: boolean; itemsCount?: number | null }
		| null
	> | null;
	parkingSessionsInsideOnReservations?: Array<CountNode | null> | null;
	parkingSessionsInsideOnContracts?: {
		nodes?: Array<{ entryDate: string; itemsCount?: number | null } | null> | null;
	} | null;
	parkingSessionsActiveReservations?: Array<CountNode | null> | null;
	insideReservationSessions?: {
		nodes?: InsideReservationSession[] | null;
	} | null;
}

interface DashboardMetricsVariables {
	startOfToday: string;
	endOfToday: string;
	entriesWindowStart: string;
	insideListLimit?: number;
}

const sumCounts = (items?: Array<CountNode | null> | null): number =>
	items?.reduce((total, item) => total + (item?.itemsCount ?? 0), 0) ?? 0;

const normalizeDateKey = (value?: string | null): string | null => {
	if (!value) return null;
	const parsed = parseISO(value);
	if (!isValid(parsed)) return null;
	return startOfDay(parsed).toISOString();
};

const DashboardStatCard = ({
	label,
	value,
	icon: Icon,
	hint
}: {
	label: string;
	value: string;
	icon: React.ComponentType<{ className?: string }>;
	hint?: string;
}) => {
	return (
		<Card className="h-full shadow-sm">
			<CardHeader className="pb-2">
				<div className="flex items-start justify-between gap-3">
					<div className="space-y-1">
						<CardTitle className="text-sm text-muted-foreground">
							{label}
						</CardTitle>
						<CardDescription className="text-3xl font-semibold text-foreground">
							{value}
						</CardDescription>
					</div>
					<div className="rounded-full bg-primary/10 p-2 text-primary">
						<Icon className="h-5 w-5" />
					</div>
				</div>
			</CardHeader>
			{hint ? (
				<CardContent className="pt-0">
					<p className="text-xs text-muted-foreground">{hint}</p>
				</CardContent>
			) : null}
		</Card>
	);
};

export const Dashboard: React.FC = () => {
	const { t } = useTranslation(['dashboard', 'common']);
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

	const today = useMemo(() => new Date(), []);
	const startOfTodayDate = useMemo(() => startOfDay(today), [today]);
	const endOfTodayDate = useMemo(() => endOfDay(today), [today]);
	const entriesWindowStartDate = useMemo(
		() => startOfDay(subDays(today, 6)),
		[today]
	);

	const queryVariables = useMemo<DashboardMetricsVariables>(
		() => ({
			startOfToday: startOfTodayDate.toISOString(),
			endOfToday: endOfTodayDate.toISOString(),
			entriesWindowStart: entriesWindowStartDate.toISOString(),
			insideListLimit: 12
		}),
		[startOfTodayDate, endOfTodayDate, entriesWindowStartDate]
	);

	const { data, loading, error, refetch } = useQuery<
		DashboardMetricsData,
		DashboardMetricsVariables
	>(DASHBOARD_METRICS_QUERY, {
		variables: queryVariables,
		fetchPolicy: 'cache-and-network'
	});

	useEffect(() => {
		setPageHeader({
			title: t('dashboard:title'),
			description: t('dashboard:subtitle')
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, t]);

	const todayEntriesCount = useMemo(
		() => sumCounts(data?.todayEntries?.nodes),
		[data?.todayEntries?.nodes]
	);
	const todayExitsCount = useMemo(
		() => sumCounts(data?.todayExits?.nodes),
		[data?.todayExits?.nodes]
	);

	const totalInside = useMemo(
		() =>
			(data?.parkingSessionsPerStatus ?? [])
				.filter(item => item?.isInside)
				.reduce((sum, item) => sum + (item?.itemsCount ?? 0), 0),
		[data?.parkingSessionsPerStatus]
	);

	const totalExited = useMemo(
		() =>
			(data?.parkingSessionsPerStatus ?? [])
				.filter(item => item?.isInside === false)
				.reduce((sum, item) => sum + (item?.itemsCount ?? 0), 0),
		[data?.parkingSessionsPerStatus]
	);

	const totalReservations = useMemo(
		() => sumCounts(data?.parkingSessionsActiveReservations),
		[data?.parkingSessionsActiveReservations]
	);

	const totalInsideReservations = useMemo(
		() => sumCounts(data?.parkingSessionsInsideOnReservations),
		[data?.parkingSessionsInsideOnReservations]
	);

	const totalInsideContracts = useMemo(
		() => sumCounts(data?.parkingSessionsInsideOnContracts?.nodes),
		[data?.parkingSessionsInsideOnContracts?.nodes]
	);

	const entriesLast7Days = useMemo(() => {
		const entriesMap = new Map<string, number>();
		(data?.recentEntries?.nodes ?? []).forEach(node => {
			const key = normalizeDateKey(node.entryDate);
			if (!key) return;
			entriesMap.set(key, (entriesMap.get(key) ?? 0) + (node.itemsCount ?? 0));
		});

		return eachDayOfInterval({
			start: entriesWindowStartDate,
			end: startOfTodayDate
		}).map(date => {
			const key = startOfDay(date).toISOString();
			return {
				dateKey: key,
				label: format(date, 'EEE dd'),
				entries: entriesMap.get(key) ?? 0
			};
		});
	}, [data?.recentEntries?.nodes, entriesWindowStartDate, startOfTodayDate]);

	const insideReservations = useMemo(
		() =>
			(data?.insideReservationSessions?.nodes ?? []).filter(
				session => (session.reservations?.length ?? 0) > 0
			),
		[data?.insideReservationSessions?.nodes]
	);

	const renderMetricValue = (value: number) =>
		loading ? '—' : value.toLocaleString();

	return (
		<div className="space-y-4 p-2 lg:space-y-6">
			{error ? (
				<Alert variant="destructive">
					<AlertTitle>{t('dashboard:errors.title')}</AlertTitle>
					<AlertDescription>
						{t('dashboard:errors.description')}
					</AlertDescription>
				</Alert>
			) : null}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
				<DashboardStatCard
					label={t('dashboard:cards.todayEntries')}
					value={renderMetricValue(todayEntriesCount)}
					icon={LogIn}
					hint={t('dashboard:cards.todayEntriesHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.todayExits')}
					value={renderMetricValue(todayExitsCount)}
					icon={LogOut}
					hint={t('dashboard:cards.todayExitsHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.totalInside')}
					value={renderMetricValue(totalInside)}
					icon={Users}
					hint={t('dashboard:cards.totalInsideHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.totalExited')}
					value={renderMetricValue(totalExited)}
					icon={BarChart3}
					hint={t('dashboard:cards.totalExitedHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.totalReservations')}
					value={renderMetricValue(totalReservations)}
					icon={CalendarDays}
					hint={t('dashboard:cards.totalReservationsHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.insideReservations')}
					value={renderMetricValue(totalInsideReservations)}
					icon={CheckCircle2}
					hint={t('dashboard:cards.insideReservationsHint')}
				/>
				<DashboardStatCard
					label={t('dashboard:cards.insideContracts')}
					value={renderMetricValue(totalInsideContracts)}
					icon={Ticket}
					hint={t('dashboard:cards.insideContractsHint')}
				/>
			</div>

			<Card className="shadow-sm">
				<CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<CardTitle>{t('dashboard:chart.title')}</CardTitle>
						<CardDescription>
							{t('dashboard:chart.subtitle')}
						</CardDescription>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							setSelectedSessionId(null);
							void refetch();
						}}
						className="h-9"
					>
						{t('dashboard:actions.refresh')}
					</Button>
				</CardHeader>
				<CardContent>
					<ChartContainer
						config={{
							entries: {
								label: t('dashboard:chart.series.entries'),
								color: 'hsl(var(--chart-1))'
							}
						}}
						className="h-[320px]"
					>
						<AreaChart data={entriesLast7Days} margin={{ top: 10, left: 0, right: 10 }}>
							<CartesianGrid strokeDasharray="3 3" className="stroke-border/80" />
							<XAxis
								dataKey="label"
								tickLine={false}
								axisLine={false}
								tickMargin={8}
								minTickGap={16}
							/>
							<YAxis
								allowDecimals={false}
								width={48}
								tickLine={false}
								axisLine={false}
							/>
							<ChartTooltip
								cursor={{ stroke: 'var(--border)' }}
								content={<ChartTooltipContent labelKey="entries" />}
							/>
							<Area
								type="monotone"
								dataKey="entries"
								stroke="var(--color-entries)"
								fill="var(--color-entries)"
								fillOpacity={0.16}
								activeDot={{ r: 6 }}
								name={t('dashboard:chart.series.entries')}
							/>
						</AreaChart>
					</ChartContainer>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader>
					<CardTitle>{t('dashboard:reservations.title')}</CardTitle>
					<CardDescription>
						{t('dashboard:reservations.subtitle')}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					{insideReservations.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{loading
								? t('dashboard:reservations.loading')
								: t('dashboard:reservations.empty')}
						</p>
					) : (
						<div className="space-y-3">
							{insideReservations.map(session => {
								const reservation = session.reservations?.[0];
								return (
									<div
										key={session.parkingSessionId}
										className="flex flex-col gap-3 rounded-lg border border-border/70 bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
									>
										<div className="space-y-2">
											<div className="flex flex-wrap items-center gap-2">
												<Badge variant="outline" className="text-sm">
													{session.vehicle?.plateNumber ?? t('dashboard:labels.unknownPlate')}
												</Badge>
												{session.ticketId ? (
													<span className="text-xs text-muted-foreground">
														{t('dashboard:labels.ticket', { ticket: session.ticketId })}
													</span>
												) : null}
											</div>
											<p className="text-sm font-medium text-foreground">
												{formatCustomerName(
													session.customer?.isCompany,
													session.customer?.firmName,
													session.customer?.firstName,
													session.customer?.lastName
												) ?? t('dashboard:labels.anonymousCustomer')}
											</p>
											<p className="text-xs text-muted-foreground">
												{t('dashboard:labels.entryAt', {
													time:
														formatDateTime(session.entryDateTime) ?? '—'
												})}
											</p>
											{reservation ? (
												<p className="text-xs text-muted-foreground">
													{t('dashboard:labels.reservationWindow', {
														start:
															formatDateTime(
																reservation.reservationStartTime
															) ?? '—',
														end:
															formatDateTime(
																reservation.reservationEndTime
															) ?? '—'
													})}
												</p>
											) : null}
										</div>
										<div className="flex flex-wrap items-center gap-2">
											{reservation?.reservationStatus?.name ? (
												<Badge variant="secondary">
													{reservation.reservationStatus.name}
												</Badge>
											) : null}
											<Button
												variant="outline"
												size="sm"
												onClick={() => setSelectedSessionId(session.parkingSessionId)}
												className="h-9"
											>
												<Link2 className="mr-2 h-4 w-4" />
												{t('dashboard:actions.viewDetails')}
											</Button>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</CardContent>
			</Card>

			<Dialog open={Boolean(selectedSessionId)} onOpenChange={() => setSelectedSessionId(null)}>
				<DialogContent size="lg" enablePreRender>
					<DialogHeader>
						<DialogTitle>
							{t('dashboard:actions.viewDetails')}
						</DialogTitle>
					</DialogHeader>
					{selectedSessionId ? (
						<ParkingSessionDetailsQuery parkingSessionId={selectedSessionId} />
					) : null}
				</DialogContent>
			</Dialog>
		</div>
	);
};
